import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';

import { useAuth } from '../lib/auth-context';
import { Spinner } from './ui';

/**
 * Gate for authenticated routes.
 *
 * Waiting for `status === 'ready'` matters: the session is restored
 * asynchronously from the refresh cookie, so rendering earlier would bounce a
 * signed-in user to /login on every page load.
 *
 * This is a UX control, not a security boundary — the API authorises every
 * request independently.
 */
export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { user, status } = useAuth();
  const location = useLocation();

  if (status === 'loading') {
    return <Spinner label="Restoring session" />;
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  return <>{children}</>;
}
